import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Bot, User as UserIcon, Loader2, RefreshCw } from 'lucide-react';
import { ChatMessage, Idea, Topic } from '../types';
import { generateAIResponse } from '../services/gemini';
import { api } from '../services/api';
import { CHAT_PAGE_SIZE, AI_MODEL_NAME, AI_MODEL_FLASH } from '../constants';

interface ChatInterfaceProps {
  ideas: Idea[];
  topics: Topic[];
  onRefresh: () => void;
  isRefreshing: boolean;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ ideas, topics, onRefresh, isRefreshing }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [model, setModel] = useState(AI_MODEL_NAME);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const fetchHistory = useCallback(async (pageNum: number) => {
    setIsFetchingHistory(true);
    try {
      const history = await api.getChats(pageNum);
      if (history.length < CHAT_PAGE_SIZE) {
        setHasMore(false);
      }
      
      // If page 1, replace. If >1, prepend.
      setMessages(prev => {
        // Simple de-dupe based on ID if overlap occurs
        const newIds = new Set(history.map(m => m.id));
        const filteredPrev = prev.filter(p => !newIds.has(p.id));
        // Sort by timestamp just in case
        const combined = [...history, ...filteredPrev].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        return combined;
      });
    } catch (e) {
      console.error("Failed to load chat history", e);
    } finally {
      setIsFetchingHistory(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchHistory(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto scroll to bottom on new user message (not history load)
  useEffect(() => {
    if (page === 1 && !isFetchingHistory) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, page, isFetchingHistory]);

  // Handle scroll up for pagination
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    if (chatContainerRef.current.scrollTop === 0 && hasMore && !isFetchingHistory) {
      scrollPositionRef.current = chatContainerRef.current.scrollHeight;
      const nextPage = page + 1;
      setPage(nextPage);
      fetchHistory(nextPage).then(() => {
        // Restore scroll position roughly
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight - scrollPositionRef.current;
        }
      });
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userText = input.trim();
    setInput('');
    setIsLoading(true);

    // Optimistic UI update
    const tempUserMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: userText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      // 1. Save user msg to DB
      await api.addChat({ role: 'user', content: userText });

      // 2. Get AI Response
      const aiText = await generateAIResponse(userText, { ideas, topics }, model);

      // 3. Save AI msg to DB
      const aiMsg = await api.addChat({ role: 'model', content: aiText });

      setMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error(error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'model',
        content: "Sorry, I couldn't save that message or get a response. Please check your network.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b bg-gray-50 flex items-center justify-between shadow-sm z-10 flex-none">
        <div className="flex items-center space-x-3">
            <div className="bg-secondary p-1.5 rounded-lg">
               <Bot className="text-white" size={20} />
            </div>
            <div>
                <h3 className="font-bold text-gray-800">Content AI</h3>
            </div>
        </div>

        <div className="flex items-center space-x-3">
             <div className="flex bg-gray-200 p-1 rounded-lg text-[10px] font-bold">
                <button 
                    onClick={() => setModel(AI_MODEL_NAME)} 
                    className={`px-3 py-1 rounded-md transition-all ${model === AI_MODEL_NAME ? 'bg-white text-secondary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Pro
                </button>
                <button 
                    onClick={() => setModel(AI_MODEL_FLASH)} 
                    className={`px-3 py-1 rounded-md transition-all ${model === AI_MODEL_FLASH ? 'bg-white text-secondary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Flash
                </button>
             </div>
             
             <button 
                onClick={onRefresh} 
                disabled={isRefreshing}
                className="p-1.5 bg-white text-gray-400 hover:text-primary rounded-full border border-gray-200 shadow-sm transition-all disabled:opacity-50"
                title="Reload Chat History"
            >
                <RefreshCw size={16} className={`${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
        </div>
      </div>

      <div 
        className="flex-1 overflow-y-auto p-4 space-y-5 bg-white scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent"
        ref={chatContainerRef}
        onScroll={handleScroll}
      >
        {isFetchingHistory && (
            <div className="flex justify-center py-2">
                <Loader2 className="animate-spin text-gray-400" size={16} />
            </div>
        )}
        
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex max-w-[85%] md:max-w-[75%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center mx-2 shadow-sm ${msg.role === 'user' ? 'bg-red-100' : 'bg-blue-100'}`}>
                {msg.role === 'user' ? <UserIcon size={16} className="text-primary" /> : <Bot size={16} className="text-secondary" />}
              </div>
              
              <div className={`p-3.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                msg.role === 'user' 
                  ? 'bg-primary text-white rounded-tr-none' 
                  : 'bg-gray-50 text-gray-800 border border-gray-100 rounded-tl-none'
              }`}>
                {msg.role === 'user' ? (
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                ) : (
                  <div className="markdown-body prose prose-sm max-w-none prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-table:my-2">
                    <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                            table: ({node, ...props}) => <table className="border-collapse border border-gray-300 w-full my-2 text-xs" {...props} />,
                            th: ({node, ...props}) => <th className="border border-gray-300 px-2 py-1 bg-gray-100" {...props} />,
                            td: ({node, ...props}) => <td className="border border-gray-300 px-2 py-1" {...props} />,
                            ul: ({node, ...props}) => <ul className="list-disc ml-4" {...props} />,
                            ol: ({node, ...props}) => <ol className="list-decimal ml-4" {...props} />,
                            strong: ({node, ...props}) => <strong className="font-bold text-secondary" {...props} />
                        }}
                    >
                        {msg.content}
                    </ReactMarkdown>
                  </div>
                )}
                <span className={`text-[10px] block mt-1.5 ${msg.role === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 bg-gray-50 border-t border-gray-100 flex-none">
        <div className="relative flex items-end bg-white border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition-all shadow-sm">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
                if(e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                }
            }}
            placeholder="Ask AI for content ideas..."
            rows={3}
            className="w-full pl-4 pr-12 py-3 bg-transparent border-none rounded-xl focus:ring-0 outline-none resize-none text-sm text-gray-800 placeholder-gray-400 overflow-y-auto"
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className="absolute right-2 bottom-2 p-2 bg-primary text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:hover:bg-primary transition-colors shadow-sm"
          >
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};